import { z } from "zod";
import { AttentionTierSchema } from "./attention.js";
import { ConsentScopeSchema } from "./consent.js";
import { SignalSourceSchema } from "./signals.js";
/**
 * D1 INSERT SCHEMAS - the PHI boundary.
 *
 * Every row that goes into D1 passes through one of these schemas. They
 * exist so a refactor can never accidentally write a raw clinical value
 * (mg, mmol/L, kg, BPM, ICD-coded condition, free-text reflection) onto
 * Cloudflare. The Cloudflare BAA covers CDN/WAF/Access/Zero Trust - NOT
 * Workers, D1, Durable Objects, R2 (CLAUDE.md). PHI lives in Azure
 * Postgres under BAA, referenced from D1 by opaque `phiRef` only.
 *
 * The schemas use Zod's `.strict()` so unknown fields are rejected at
 * the type level - if someone adds a column to a migration without
 * updating the schema here, every insert fails loudly. That's the
 * intended posture: an explicit "yes this column is non-PHI" gate.
 *
 * Booleans, ULIDs/UUIDs, ISO-8601 timestamps, scope enums, source enums,
 * tier enums, manifest versions, opaque phi_refs - all fine. Anything
 * that looks like a numeric value of a clinical kind, free-text mood,
 * or named medication does NOT belong in D1.
 *
 * If you find yourself adding a `value: number` field to one of these,
 * stop. The value belongs in Azure. Add a `phi_ref` field instead.
 */
const Iso8601 = z.string().datetime({ message: "must be ISO-8601 datetime" });
const Uuid = z.string().uuid();
const Ulid = z
    .string()
    .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, { message: "must be a ULID (Crockford base32)" });
/**
 * `phiRef`: opaque, stable token referencing the actual value in Azure
 * Postgres. The format is intentionally generic - Azure side can use any
 * scheme as long as it's reversible only with proper auth and never
 * embeds the value or its scale. Bounded length so D1 storage is
 * predictable.
 */
const PhiRef = z.string().min(1).max(120);
// ---------------------------------------------------------------------------
// consent_grants - ledger of every grant + revocation.
// ---------------------------------------------------------------------------
export const ConsentGrantInsertSchema = z
    .object({
    id: Ulid,
    user_id: z.string().min(1).max(120),
    scope: ConsentScopeSchema,
    granted_at: Iso8601,
    revoked_at: Iso8601.optional(),
    purpose: z.string().min(1).max(500),
    retention_days: z.number().int().min(0).max(3650),
    source: z.enum(["onboarding", "settings", "partner-portal", "legal-admin"]),
    partner_id: z.string().min(1).max(120).optional(),
    created_at: Iso8601,
})
    .strict();
// ---------------------------------------------------------------------------
// consent_violation_attempts - audit row when requireConsent rejects.
// ---------------------------------------------------------------------------
export const ConsentViolationAttemptInsertSchema = z
    .object({
    id: Ulid,
    user_id: z.string().min(1).max(120),
    scope: ConsentScopeSchema,
    route: z.string().min(1).max(200),
    attempted_at: Iso8601,
    reason: z.enum(["missing", "revoked", "expired"]),
})
    .strict();
// ---------------------------------------------------------------------------
// attention_events - Heartbeat output.
// ---------------------------------------------------------------------------
export const AttentionEventInsertSchema = z
    .object({
    id: Uuid,
    user_id: z.string().min(1).max(120),
    persona_id: z.string().min(1).max(80),
    manifest_version: z.string().regex(/^\d+\.\d+\.\d+$/),
    triggering_rule_id: z.string().min(1).max(80),
    tier: AttentionTierSchema,
    surface: z.enum(["companion-web", "companion-mobile", "email", "push", "sms", "partner-embed"]),
    reason: z.string().min(1).max(280),
    /** Bounded, structured-string evidence summary; serialised JSON array of
     *  `{ kind, summary }` per docs. Each summary <= 240 chars and MUST NOT
     *  include the raw signal value. */
    evidence_summary: z.string().max(2400).optional(),
    proposed_action_plan_id: Uuid.optional(),
    scheduled_for: Iso8601,
    expires_at: Iso8601,
    consumed_at: Iso8601.optional(),
    suppressed_by: z
        .enum([
        "quiet-hours",
        "daily-budget",
        "per-surface-cap",
        "crisis-override",
        "consent-revoked",
    ])
        .optional(),
    created_at: Iso8601,
})
    .strict();
// ---------------------------------------------------------------------------
// delivery_receipts - what shipped (vs intent in attention_events).
// ---------------------------------------------------------------------------
export const DeliveryReceiptInsertSchema = z
    .object({
    id: Uuid,
    user_id: z.string().min(1).max(120),
    persona_id: z.string().min(1).max(80),
    attention_event_id: Uuid.optional(),
    action_plan_id: Uuid,
    surface: z.enum(["companion-web", "companion-mobile", "email", "push", "sms", "partner-embed"]),
    status: z.enum(["delivered", "failed", "skipped"]),
    reason: z.string().max(240).optional(),
    delivered_at: Iso8601,
    acknowledged_at: Iso8601.optional(),
    acted_on_at: Iso8601.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// heartbeat_rule_outcomes - calibration boolean ledger.
// ---------------------------------------------------------------------------
export const HeartbeatRuleOutcomeInsertSchema = z
    .object({
    id: Uuid,
    user_id: z.string().min(1).max(120),
    persona_id: z.string().min(1).max(80),
    manifest_version: z.string().regex(/^\d+\.\d+\.\d+$/),
    rule_id: z.string().min(1).max(80),
    rule_family: z.enum(["presence", "threshold", "absence"]),
    /** The only outcome we keep here. Stored as 0|1 in SQLite; expose as
     *  number so the schema rejects truthy strings. */
    crossed: z.number().int().min(0).max(1),
    evaluated_at: Iso8601,
    /** Opaque ref to the value in Azure that drove `crossed`. NULL when the
     *  rule fired on absence (no value to reference). */
    phi_ref: PhiRef.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// dose_events - the adherence ledger. Measurement-thesis core.
// ---------------------------------------------------------------------------
export const DoseEventInsertSchema = z
    .object({
    id: Uuid,
    user_id: z.string().min(1).max(120),
    persona_id: z.string().min(1).max(80),
    /** Drug class; 'glp1' is the wedge, but the column is open so the same
     *  table generalises to other adherence-driven personas. */
    drug_class: z.string().min(1).max(40),
    /** The adherence boolean. The DOSE AMOUNT (mg) is PHI and lives in
     *  Azure under BAA. Never put a number-of-mg here. */
    taken: z.number().int().min(0).max(1),
    source: SignalSourceSchema,
    observed_at: Iso8601,
    recorded_at: Iso8601,
    /** Which consent grant authorised this write. Required so the audit
     *  trail can prove every dose event was consented. */
    consent_grant_id: Ulid,
    phi_ref: PhiRef.optional(),
    schema_version: z.string().regex(/^\d+\.\d+\.\d+$/),
})
    .strict();
// ---------------------------------------------------------------------------
// sleep_summary - per-night duration ledger for the sleep persona.
//
// Per ADR-0004, per-night sleep duration in minutes from a consumer
// wearable is operational telemetry, not PHI. This schema is the type-
// level enforcement of that scope: minutes_slept is INTEGER (no decimal
// hours, no fractional seconds), there is no `value` field for a
// clinical reading, no diagnostic code, no clinician note. Adding any
// such column would force this schema to update - `.strict()` rejects
// unknown fields at parse time, so the boundary moves explicitly or
// not at all.
// ---------------------------------------------------------------------------
export const SleepSummaryInsertSchema = z
    .object({
    id: Uuid,
    user_id: z.string().min(1).max(120),
    persona_id: z.string().min(1).max(80),
    /** YYYY-MM-DD local date the user went to bed for. Sleep that crosses
     *  midnight attributes to the earlier date (e.g. 23:30 → 07:30 lands
     *  under the date the user lay down). Ingestion-worker bucketing
     *  decides this; the schema only enforces the calendar shape. */
    night_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "must be YYYY-MM-DD" }),
    /** Duration in minutes. INTEGER (no decimal hours, no fractional
     *  seconds) so the column never carries the granularity of a
     *  clinical reading. Range: 0 to 1440 (a full day). */
    minutes_slept: z
        .number()
        .int()
        .min(0)
        .max(24 * 60),
    source: SignalSourceSchema,
    recorded_at: Iso8601,
    /** Which consent grant authorised this write. Required so the audit
     *  trail can prove every sleep row was consented. */
    consent_grant_id: Ulid,
    schema_version: z.string().regex(/^\d+\.\d+\.\d+$/),
})
    .strict();
/**
 * Discriminated union over every legal D1 insert shape. Use this when a
 * worker has a generic write helper; concrete call sites should prefer
 * the table-specific schema for better error messages.
 */
export const D1InsertSchema = z.discriminatedUnion("table", [
    z.object({ table: z.literal("consent_grants"), row: ConsentGrantInsertSchema }),
    z.object({
        table: z.literal("consent_violation_attempts"),
        row: ConsentViolationAttemptInsertSchema,
    }),
    z.object({ table: z.literal("attention_events"), row: AttentionEventInsertSchema }),
    z.object({ table: z.literal("delivery_receipts"), row: DeliveryReceiptInsertSchema }),
    z.object({
        table: z.literal("heartbeat_rule_outcomes"),
        row: HeartbeatRuleOutcomeInsertSchema,
    }),
    z.object({ table: z.literal("dose_events"), row: DoseEventInsertSchema }),
    z.object({ table: z.literal("sleep_summary"), row: SleepSummaryInsertSchema }),
]);
//# sourceMappingURL=d1.js.map